import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import type { FieldWorkRequest } from '../../domain/fieldWorkRequest';

/** Site and customer names by id, so requests can be found by where they are. */
export interface FieldWorkPlaceNames {
  sites: ReadonlyMap<string, string>;
  customers: ReadonlyMap<string, string>;
}

export const NO_PLACE_NAMES: FieldWorkPlaceNames = { sites: new Map(), customers: new Map() };

/**
 * Whether a request matches a free-text search: title, description,
 * assignee, requester, site or customer (name or display name). Blank
 * matches everything.
 */
export function matchesFieldWorkSearch(
  request: FieldWorkRequest,
  query: string,
  names: FieldWorkPlaceNames = NO_PLACE_NAMES,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    request.title,
    request.description,
    request.assignee,
    request.requestedBy ?? '',
    request.siteId ? (names.sites.get(request.siteId) ?? '') : '',
    request.customerId ? (names.customers.get(request.customerId) ?? '') : '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

/**
 * Site and customer names for searching. Uses the shared sites and
 * customers queries; until they load, search simply ignores places.
 */
export function useFieldWorkPlaceNames(): FieldWorkPlaceNames {
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  return useMemo(
    () => ({
      sites: new Map(
        (sites.data ?? []).map((site) => [site.id, `${site.name} ${site.displayName ?? ''}`]),
      ),
      customers: new Map(
        (customers.data ?? []).map((customer) => [
          customer.id,
          `${customer.name} ${customer.displayName ?? ''}`,
        ]),
      ),
    }),
    [sites.data, customers.data],
  );
}
